import { useState, FC, useRef, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Slider, Stack, Typography } from 'merinio-mui'

import Input from 'components/Forms/Input'
import { Address, SearchAddress, AutomaticButtonGroup } from 'components/UI'

import { Button as MerinioButton } from 'ui'
import { Map } from './AddressMap'

import { t } from 'translations'

// Display the entered address

type Props = {
  onChange: (address: Partial<MData.Address>) => void
  address?: MData.Address
  disabled?: boolean
  noBackGround?: boolean
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void
  enableRadius?: boolean
}

const AddressForm: FC<Props> = ({ address, disabled, onChange, noBackGround, enableRadius, setFieldValue }) => {
  const { enablePunchGeolocation } = useSelector((state: MStore.RootState) => state.settings)
  const [isEditingAddress, setIsEditingAddress] = useState(false)

  const hasAnAddress = (address?: Partial<MData.Address>) => {
    const { room, floor, street_number, route, locality, administrative_area_level_1, postal_code, country } = address || {}
    return room || floor || street_number || route || locality || administrative_area_level_1 || postal_code || country
  }

  useEffect(() => {
    if (!address?.radius) setFieldValue('address.radius', 100)
  }, [setFieldValue, address?.radius])

  const hasAddress = hasAnAddress(address)

  const radius = address?.radius ?? 100

  const coordinates = useMemo(
    () =>
      address?.latitude && address?.longitude
        ? {
            latitude: address.latitude,
            longitude: address.longitude
          }
        : null,
    [address]
  )

  const handleSetRadius = event => {
    setFieldValue('address.radius', parseInt(event.target.value))
  }

  return (
    <div className={noBackGround ? `mb-2` : 'card bg-light mb-3'}>
      <div className={noBackGround ? 'mr-3' : 'p-3'}>
        <AddOrUpdateAddressForm isEditingAddress={isEditingAddress} disabled={disabled} onChange={onChange} />
        <div className="d-flex flex-row justify-content-between">
          <div>{!isEditingAddress ? <Address address={address} /> : null}</div>
          <EditControls
            isEditingAddress={isEditingAddress}
            hasAddress={hasAddress}
            setIsEditingAddress={setIsEditingAddress}
            setFieldValue={setFieldValue}
            disabled={disabled}
          />
        </div>
        {enableRadius ? (
          <RangeSlider
            enablePunchGeolocation={enablePunchGeolocation}
            isEditingAddress={isEditingAddress}
            hasAddress={hasAddress}
            value={radius}
            handleChange={handleSetRadius}
          />
        ) : null}
        {isEditingAddress || hasAddress ? <Map coordinates={coordinates} radius={enablePunchGeolocation ? radius : null} width="100%" /> : null}
        <AddOrUpdateControls
          address={address}
          setFieldValue={setFieldValue}
          setIsEditingAddress={setIsEditingAddress}
          isEditingAddress={isEditingAddress}
          disabled={disabled}
        />
        <AddNewControls isEditingAddress={isEditingAddress} hasAddress={hasAddress} disabled={disabled} setIsEditingAddress={setIsEditingAddress} />
      </div>
    </div>
  )
}

export default AddressForm

const RangeSlider = ({ value, handleChange, isEditingAddress, hasAddress, enablePunchGeolocation }) => {
  if (!enablePunchGeolocation) return null
  if (!hasAddress && !isEditingAddress) return null
  return (
    <Stack spacing={2} direction="row" sx={{ my: 2 }} alignItems="center">
      <Typography>
        {t('components.address.GEOFENCE_RADIUS')}:{' '}
        {isEditingAddress ? <input className="merinio-inline-input" type="number" value={value} onChange={handleChange} style={{ width: '4rem' }} /> : value}m
      </Typography>
      {isEditingAddress ? (
        <Slider defaultValue={100} max={1000} sx={{ width: 200, color: '#009775' }} aria-label="Radius" value={value} onChange={handleChange} />
      ) : null}
    </Stack>
  )
}

const AddOrUpdateControls = ({ address, setIsEditingAddress, isEditingAddress, disabled, setFieldValue }) => {
  const addressRef = useRef(address)
  const hasPreviousAddress = Boolean(Object.keys(addressRef.current).length)
  if (!isEditingAddress || disabled) return null

  const handleAddOrUpdate = () => {
    setIsEditingAddress(false)
    addressRef.current = address
  }

  const handleCancel = () => {
    setIsEditingAddress(false)
    setFieldValue('address', addressRef.current)
  }

  return (
    <div className="mt-1">
      <MerinioButton type="button" color="secondary" onClick={handleAddOrUpdate} outlined disabled={!Object.keys(address).length}>
        {hasPreviousAddress ? t('components.address.UPDATE') : t('components.address.ADD')}
      </MerinioButton>
      <MerinioButton className="ml-1" type="button" color="secondary" onClick={handleCancel} outlined>
        {t('components.address.CANCEL')}
      </MerinioButton>
    </div>
  )
}

const AddNewControls = ({ disabled, setIsEditingAddress, hasAddress, isEditingAddress }) => {
  if (hasAddress || isEditingAddress) return null
  return (
    <>
      <p>{t('components.address.NO_ADDRESS_ENTERED')}</p>
      {!disabled && (
        <MerinioButton type="button" color="secondary" onClick={() => setIsEditingAddress(true)} outlined>
          {t('components.address.ADD_AN_ADDRESS')}
        </MerinioButton>
      )}
    </>
  )
}

const EditControls = ({ setIsEditingAddress, setFieldValue, disabled, hasAddress, isEditingAddress }) => {
  if (disabled || !hasAddress || isEditingAddress) return null
  return (
    <div>
      <AutomaticButtonGroup>
        <MerinioButton type="button" color="secondary" onClick={() => setIsEditingAddress(true)} outlined>
          {t('components.address.EDIT_ADDRESS')}
        </MerinioButton>
        <MerinioButton type="button" color="secondary" onClick={() => setFieldValue('address', {})} disabled={disabled} outlined>
          <i className="fas fa-trash"></i>
        </MerinioButton>
      </AutomaticButtonGroup>
    </div>
  )
}

const AddOrUpdateAddressForm = ({ onChange, isEditingAddress, disabled }) => {
  if (!isEditingAddress || disabled) return null
  return (
    <>
      <SearchAddress onSelect={address => onChange(address)} />
      <div className="mb-3 border-bottom" />
      <Input module="administration.tags.form" name="address.room" />
      <Input module="administration.tags.form" name="address.floor" />
      <Input module="administration.tags.form" name="address.street_number" />
      <Input module="administration.tags.form" name="address.route" />
      <Input module="administration.tags.form" name="address.locality" />
      <Input module="administration.tags.form" name="address.administrative_area_level_1" />
      <Input module="administration.tags.form" name="address.postal_code" />
      <Input module="administration.tags.form" name="address.country" />
    </>
  )
}
